import React, { useContext, useEffect } from 'react';
import { Box, Grid, Paper, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import Linkify from 'react-linkify/dist/components/Linkify';
import Masonry from 'react-masonry-css';
import EventIcon from '@material-ui/icons/Event';
import { useParams } from 'react-router-dom';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { SecureLink } from 'react-secure-link';

import { MeetingDetailsContext } from '../../../services/meeting-details/meeting-details.context';
import { CommentContextProvider } from '../../../services/meeting-details/meeting-details-comment.context';

import { useMeetingDetailsStyle } from '../components/meeting-details.style';

import { MeetingDetailMap } from '../components/map/meeting-detail-map';
import { ProfileContext } from '../../../services/profile/profile.context';
import { InvitedUsersComponent } from '../components/invited-users';
import { MeetingStatus } from '../components/meeting-status';
import { JoinZoomComponent } from '../components/join-zoom';
import NotFound from '../../../Pages/NotFound';
import { CommentContainer } from '../components/comments/comment-container';

const breakpointColumnsObj = {
  default: 2,
  922: 1,
};

export const MeetingDetails = ({ state = null }) => {
  const classes = useMeetingDetailsStyle();
  const { id } = useParams();

  const { status: statusState, meeting: meetingState } = state;
  const { user } = useContext(ProfileContext);
  const { states, functions } = useContext(MeetingDetailsContext);

  const {
    getMeeting,
    copyLink,
    dateTime,
    getUsersData,
    selectRequest,
  } = functions;

  const {
    setMeeting,
    status,
    setStatus,
    error,
    meeting,
    isCopy,
    meetingUsers,
  } = states;

  useEffect(() => {
    if (state) {
      setStatus(statusState);

      setMeeting(meetingState);

      meetingState && getUsersData(meetingState);
      id && user && getMeeting(id);
    }
  }, [state, id, user, statusState]);

  useEffect(() => {
    if (!state) {
      id && user && getMeeting(id);
    }
  }, [state, id, user]);

  return (
    <Paper>
      {error ? (
        <NotFound />
      ) : (
        <>
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className='my-masonry-grid'
            columnClassName='my-masonry-grid_column'
          >
            <Box boxShadow={5} className={classes.imageContainer}>
              {meeting && meeting.meetingphoto && meeting.meetingphoto.image ? (
                <img
                  className={classes.meetingImage}
                  src={meeting.meetingphoto.image}
                  alt='meeting'
                />
              ) : (
                !error && <Skeleton variant='rect' width='100%' height={300} />
              )}
            </Box>

            <Box my={1} px={1}>
              <Box>
                <Box dir='auto'>
                  <Typography variant='body1' gutterBottom>
                    {meeting && meeting.title}
                  </Typography>
                </Box>
                <Box dir='auto' overflow='auto' maxHeight={114}>
                  <Typography className={classes.description} variant='body2'>
                    {meeting && (
                      <Linkify
                        componentDecorator={(
                          decoratedHref,
                          decoratedText,
                          key
                        ) => (
                          <SecureLink href={decoratedHref} key={key}>
                            {decoratedText}
                          </SecureLink>
                        )}
                      >
                        {meeting.description}
                      </Linkify>
                    )}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={1}>
                <Grid item xs={12}>
                  {meeting && meeting.date_time && (
                    <Box display='flex' alignItems='center' mt={2}>
                      <EventIcon color='secondary' className={classes.icon} />
                      <Typography variant='body2' className={classes.label}>
                        Start:
                      </Typography>
                      <Typography variant='body1'>
                        {dateTime(meeting.date_time)}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  {meeting && meeting.end_date_time && (
                    <Box display='flex' alignItems='center'>
                      <EventIcon color='secondary' className={classes.icon} />
                      <Typography variant='body2' className={classes.label}>
                        End:
                      </Typography>
                      <Typography variant='body1'>
                        {meeting &&
                          meeting.end_date_time &&
                          dateTime(meeting.end_date_time)}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>

              {/* {meeting && meeting.url && (
                <Box display='flex' alignItems='center'>
                  <LinkIcon color='secondary' className={classes.icon} />
                  <Typography variant='body2' className={classes.label}>
                    Virtual meeting room:
                  </Typography>
                  <a
                    href={meetingLink(meeting.url)}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {meeting.url}
                  </a>
                </Box>
              )} */}
              <Box
                display='flex'
                my={2}
                alignItems='center'
                className={classes.copyLink}
                onClick={copyLink}
              >
                <FileCopyIcon color='secondary' className={classes.icon} />
                <Typography variant='body2' className={classes.label}>
                  {isCopy ? 'Copied!' : ' Copy meeting link '}
                </Typography>
              </Box>
            </Box>
          </Masonry>

          <Masonry
            breakpointCols={breakpointColumnsObj}
            className='my-masonry-grid'
            columnClassName='my-masonry-grid_column'
          >
            <Box height={300}>
              {meetingUsers && <MeetingDetailMap meetingUsers={meetingUsers} />}
            </Box>

            <Box py={2} px={1}>
              <InvitedUsersComponent
                meetingUsers={meetingUsers}
                meeting={meeting}
                user={user}
                selectRequest={selectRequest}
              />

              <Box>{status !== '' && status !== null && <MeetingStatus />}</Box>

              <Box mt={5}>
                {status !== '' && status !== null && (
                  <JoinZoomComponent
                    user={user}
                    id={id}
                    status={status}
                    meeting={meeting}
                  />
                )}
              </Box>
            </Box>
          </Masonry>
        </>
      )}

      <CommentContextProvider>
        {meeting && meeting.id && <CommentContainer m_id={meeting.id} />}
      </CommentContextProvider>
    </Paper>
  );
};
